// Atomic Mode orchestration: take a paused sandbox + a selected hotspot and
// produce (a) the bare atomify iframe URL and (b) the postMessage payload that
// loads the equivalent molecular-dynamics system into it. Glue only — the
// science lives in `lammps.ts`, the message contract in `atomifyEmbed.ts`, the
// periodic data in `elements.ts`.

import bundledYaml from "../../../../packages/material-db/data/bundled.yaml?raw";
import {
  buildLoadSimulationMessage,
  type LoadSimulationMessage,
} from "./atomifyEmbed";
import type { CrystalStructure } from "./crystal";
import { parseElements } from "./elements";
import { fallbackComposition, type MaterialLike } from "./fallbackComposition";
import {
  buildHybridCarbonSystem,
  buildLammpsSystem,
  writeDataFile,
  writeInputScript,
  type ConverterContext,
  type GridRegion,
  type GridSnapshot,
} from "./lammps";
import {
  selectReactiveField,
  selectRecipe,
  writeHybridCarbonScript,
  writeReactiveScript,
  writeRecipeScript,
} from "./recipes";
import {
  crystalForMaterial,
  type CrystalComposition,
  type MaterialCrystal,
} from "./structures";
import type { MaterialDef, SandboxLike } from "./types";

// Parsed once: the periodic-table rows (symbol -> z, atomic_weight).
const ELEMENTS = parseElements(bundledYaml);

/** Where the wrapped atomify bundle is deployed. */
export const ATOMIFY_BASE_URL: string =
  (import.meta.env.VITE_ATOMIFY_URL as string | undefined)?.replace(
    /\/$/,
    "",
  ) ?? "https://coscience-atomify.pages.dev";

/**
 * Target ceiling on the number of atoms in a hotspot run. The in-browser WASM
 * LAMMPS gets slow to load/run past ~150 atoms, so this is deliberately low.
 * Override with VITE_ATOMIC_MAX_ATOMS to raise it on a beefier machine — the
 * converter downsamples cells/lattice while preserving each material's ratio.
 */
export const ATOMIC_MAX_ATOMS: number = (() => {
  const raw = import.meta.env.VITE_ATOMIC_MAX_ATOMS as string | undefined;
  const n = raw ? Number.parseInt(raw, 10) : NaN;
  return Number.isFinite(n) && n > 0 ? n : 150;
})();

// A selection must be at least this fraction one material to be treated as a
// single crystal; below it, the cubic stoichiometric packing is used so a
// genuinely mixed selection (e.g. sand + water) keeps all its materials.
const SINGLE_MATERIAL_FRACTION = 0.9;

/**
 * Resolve the real crystal structure of the selection's dominant material, so
 * its atomic structure is preserved even on the UFF fallback. Returns null for
 * genuinely mixed selections (use cubic packing) or materials with no single
 * crystal (mixtures, molecular liquids).
 */
function dominantCrystal(
  snapshot: GridSnapshot,
  region: GridRegion,
  materials: MaterialDef[],
): MaterialCrystal | null {
  const x0 = Math.max(0, Math.min(region.x0, region.x1));
  const x1 = Math.min(snapshot.width - 1, Math.max(region.x0, region.x1));
  const y0 = Math.max(0, Math.min(region.y0, region.y1));
  const y1 = Math.min(snapshot.height - 1, Math.max(region.y0, region.y1));

  const counts = new Map<number, number>();
  let total = 0;
  for (let gy = y0; gy <= y1; gy++) {
    for (let gx = x0; gx <= x1; gx++) {
      const m = snapshot.materials[gy * snapshot.width + gx];
      const mat = materials[m];
      if (!mat || mat.id === "empty") continue;
      counts.set(m, (counts.get(m) ?? 0) + 1);
      total++;
    }
  }
  if (total === 0) return null;
  const [dominantId, dCount] = [...counts.entries()].sort(
    (a, b) => b[1] - a[1],
  )[0];
  if (dCount / total < SINGLE_MATERIAL_FRACTION) return null; // mixed → cubic

  const mat = materials[dominantId] as MaterialDef & {
    composition?: { id: string; n: number }[];
    density?: number;
  };
  const raw =
    mat.composition && mat.composition.length > 0
      ? mat.composition
      : fallbackComposition(mat as MaterialLike);
  const comp: CrystalComposition[] = raw
    .map((r) => ({
      id: r.id,
      n: r.n,
      atomicWeight: ELEMENTS.get(r.id)?.atomicWeight ?? 0,
    }))
    .filter((c) => c.atomicWeight > 0);
  if (comp.length === 0) return null;
  return crystalForMaterial(comp, mat.density);
}

/** Read the paused sandbox into a plain snapshot the converter can use. */
export function snapshotSandbox(sandbox: SandboxLike): GridSnapshot {
  return {
    width: sandbox.width(),
    height: sandbox.height(),
    materials: sandbox.snapshotMaterials(),
    temperature: sandbox.snapshotTemperature(),
  };
}

export interface AtomicModeResult {
  /**
   * The atomify iframe URL. This is the BARE bundle (with `embed=true` so it
   * starts in visualizer mode) — the simulation itself is delivered separately
   * via postMessage, because a real hotspot is far larger than a URL can hold.
   */
  url: string;
  /** The simulation payload to post into the iframe once its bridge is ready. */
  message: LoadSimulationMessage;
  /** Number of atoms placed — surfaced in the UI; 0 means empty region. */
  atomCount: number;
  /** Distinct elements (atom types) in the system. */
  elementSymbols: string[];
  /** The generated LAMMPS input script (handy for debugging / display). */
  inputScript: string;
  /** Recipe id used (real potential), or null for the UFF-LJ fallback. */
  recipeId: string | null;
  /** Potential files the run depends on (fetched by the iframe bridge). */
  potentialFiles: string[];
}

/**
 * The iframe loads the bundle in embedded/visualizer mode but WITHOUT a
 * `?data=` payload — the simulation is pushed over postMessage afterwards.
 * `embed=true` keeps atomify in its chromeless visualizer layout.
 */
export function atomifyIframeUrl(): string {
  const params = new URLSearchParams();
  params.set("embed", "true");
  return `${ATOMIFY_BASE_URL}/?${params.toString()}`;
}

/**
 * Convert a hotspot of the paused sandbox into an atomify iframe URL plus the
 * postMessage payload that loads the molecular-dynamics system. Returns null
 * only if there are no convertible atoms in the region.
 */
export function buildAtomicModeRun(
  snapshot: GridSnapshot,
  region: GridRegion,
  materials: MaterialDef[],
): AtomicModeResult | null {
  const baseCtx: ConverterContext = {
    materials,
    elements: ELEMENTS,
    atomBudget: ATOMIC_MAX_ATOMS,
  };
  // First pass: discover which materials/elements are in the selection.
  const probe = buildLammpsSystem(snapshot, region, baseCtx);
  if (probe.atoms.length === 0) return null;

  // Try to match a real LAMMPS potential recipe (EAM/Vashishta/AIREBO/ReaxFF).
  const recipe = selectRecipe(probe.materialIds, probe.elementSymbols);

  const dataFileName = "hotspot.data";
  const inputName = "in.hotspot";
  const files: Record<string, string> = {};
  let potentialFiles: string[] = [];
  let inputScript: string;
  let system = probe;
  let recipeId: string | null = null;
  let dataFile: string;

  // Real crystal geometry for the dominant material (preserved even on the UFF
  // fallback). The recipe's own structure wins when it has one; otherwise we
  // resolve the material's natural lattice. Null for mixed/structureless
  // selections → cubic stoichiometric packing.
  const recipeCrystal: MaterialCrystal | null =
    recipe?.structure && recipe.lattice
      ? {
          structure: recipe.structure as CrystalStructure,
          a: recipe.lattice,
          c: recipe.latticeC,
        }
      : null;
  const crystal =
    recipeCrystal ?? dominantCrystal(snapshot, region, materials) ?? undefined;

  if (recipe) {
    // Re-place atoms so types follow the recipe's element order, then write a
    // real-potential script + collect the potential files the bridge fetches.
    system = buildLammpsSystem(snapshot, region, {
      ...baseCtx,
      forceTypeOrder: recipe.elements,
      crystal,
    });
    const masses = new Map(system.types.map((t) => [t.symbol, t.mass]));
    const rs = writeRecipeScript(
      recipe,
      dataFileName,
      masses,
      system.meanTempK,
    );
    inputScript = rs.inputScript;
    potentialFiles = rs.potentialFiles;
    recipeId = recipe.id;
    // The data file's Atoms section must match the recipe's atom_style
    // (charge styles like ReaxFF need a per-atom charge column).
    const charges = recipe.charge
      ? new Map(Object.entries(recipe.charge))
      : undefined;
    dataFile = writeDataFile(
      system,
      recipe.atomStyle === "charge" ? "charge" : "atomic",
      charges,
    );
  } else if (
    probe.elementSymbols.length > 1 &&
    selectReactiveField(probe.elementSymbols)
  ) {
    // Reactive interface: every element fits one ReaxFF field, so run the WHOLE
    // mix reactively — bonds can form/break across the material boundary (e.g.
    // graphene + water → oxidation, fuel + oxygen → combustion).
    const reactive = selectReactiveField(probe.elementSymbols)!;
    system = buildLammpsSystem(snapshot, region, {
      ...baseCtx,
      forceTypeOrder: reactive.elements,
    });
    const masses = new Map(system.types.map((t) => [t.symbol, t.mass]));
    const rs = writeReactiveScript(
      reactive.field,
      reactive.elements,
      dataFileName,
      masses,
      system.meanTempK,
    );
    inputScript = rs.inputScript;
    potentialFiles = rs.potentialFiles;
    recipeId = "reaxff_reactive";
    dataFile = writeDataFile(system, "charge");
  } else if (
    probe.elementSymbols.includes("C") &&
    probe.elementSymbols.length > 1
  ) {
    // Mixed selection containing carbon: keep the carbon as REAL graphene
    // (AIREBO honeycomb sheet) while the other elements use LJ, via a hybrid
    // run. Falls through to plain UFF if the hybrid build isn't applicable.
    const hybrid = buildHybridCarbonSystem(snapshot, region, baseCtx);
    if (hybrid) {
      system = hybrid;
      const rs = writeHybridCarbonScript(hybrid, dataFileName);
      inputScript = rs.inputScript;
      potentialFiles = rs.potentialFiles;
      recipeId = "hybrid_carbon";
      dataFile = writeDataFile(system);
    } else {
      if (crystal) {
        system = buildLammpsSystem(snapshot, region, { ...baseCtx, crystal });
      }
      inputScript = writeInputScript(system, dataFileName);
      dataFile = writeDataFile(system);
    }
  } else {
    // Universal fallback: UFF Lennard-Jones — but still build on the material's
    // real crystal lattice when it has one, so its atomic structure is kept.
    if (crystal) {
      system = buildLammpsSystem(snapshot, region, { ...baseCtx, crystal });
    }
    inputScript = writeInputScript(system, dataFileName);
    dataFile = writeDataFile(system);
  }

  files[inputName] = inputScript;
  files[dataFileName] = dataFile;

  const message = buildLoadSimulationMessage({
    id: `atomic-hotspot-${region.x0}-${region.y0}-${region.x1}-${region.y1}-${Date.now()}`,
    inputScript: inputName,
    files,
    potentialFiles,
    autostart: true,
  });

  return {
    url: atomifyIframeUrl(),
    message,
    atomCount: system.atoms.length,
    elementSymbols: system.types.map((t) => t.symbol),
    inputScript,
    recipeId,
    potentialFiles,
  };
}
